import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption, H2, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidCount,
    getItemSummaryBuyNowPrice,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsReserveMet,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';
import { getIsSearchPage } from '@/utils/urls';

export type ItemCardPriceRowSearchCardProps = {
    className?: string;
    isHovered?: boolean;
    itemId: number;
    onMouseEnter?: React.MouseEventHandler<HTMLSpanElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLSpanElement>;
    onNavigate?: () => void;
    onUserLoggedIn?: () => void;
    showBidCount?: boolean;
    shouldUseCarouselFontSize?: boolean;
};

const PRICE_LINK_CLASS = 'text-text-primary';
const PRICE_LINK_HOVERED_CLASS = '[&_span]:!cursor-pointer [&_span]:!text-link [&_span]:!underline !underline';

export enum ItemCardPriceRowDisplayKind {
    WithdrawnPaused = 'withdrawn_paused',
    ActivePrice = 'active_price',
    SoldReserveMet = 'sold_reserve_met',
    SoldNoReserve = 'sold_no_reserve',
    CatalogPreview = 'catalog_preview',
    UnsoldPending = 'unsold_pending',
    Passed = 'passed',
}

type ResolveItemCardPriceRowDisplayKindParams = {
    buyNowAvailable: boolean;
    isAvailable: boolean;
    isCatalogOnly: boolean;
    isDone: boolean;
    isLotOpen: boolean;
    isPassed: boolean;
    isPaused: boolean;
    isReserveMet: boolean;
    isSold: boolean;
    isUnsold: boolean;
};

const resolveItemCardPriceRowDisplayKind = (
    conditions: ResolveItemCardPriceRowDisplayKindParams
): ItemCardPriceRowDisplayKind | null => {
    if (!conditions.isDone && conditions.isPaused) {
        return ItemCardPriceRowDisplayKind.WithdrawnPaused;
    }
    if (
        conditions.isLotOpen ||
        conditions.buyNowAvailable ||
        (conditions.isAvailable && !conditions.isUnsold && !conditions.isPaused && !conditions.isCatalogOnly)
    ) {
        return ItemCardPriceRowDisplayKind.ActivePrice;
    }
    if (conditions.isSold && conditions.isReserveMet) {
        return ItemCardPriceRowDisplayKind.SoldReserveMet;
    }
    if (conditions.isSold && !conditions.isReserveMet) {
        return ItemCardPriceRowDisplayKind.SoldNoReserve;
    }
    if (conditions.isCatalogOnly && !conditions.isPassed) {
        return ItemCardPriceRowDisplayKind.CatalogPreview;
    }
    if (conditions.isUnsold) {
        return ItemCardPriceRowDisplayKind.UnsoldPending;
    }
    if (conditions.isPassed) {
        return ItemCardPriceRowDisplayKind.Passed;
    }
    return null;
};

export default function ItemCardPriceRowSearchCard({
    className,
    isHovered,
    itemId,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onNavigate,
    showBidCount,
    shouldUseCarouselFontSize = false,
}: ItemCardPriceRowSearchCardProps) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();

    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));

    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const isOnSearchPage = getIsSearchPage(pathname);

    const isDone = catalogStatus === 'done';
    const buyNowAvailable = buyNowStatus === BuyNowStatus.Available && isDone;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const currentPrice = leadingBid || salePrice || startPrice || 0;

    const displayKind = resolveItemCardPriceRowDisplayKind({
        buyNowAvailable,
        isAvailable,
        isCatalogOnly,
        isDone,
        isLotOpen,
        isPassed,
        isPaused,
        isReserveMet,
        isSold,
        isUnsold,
    });

    const spanProps: React.HTMLAttributes<HTMLSpanElement> = {
        onMouseEnter,
        onMouseLeave,
    };

    const itemPriceGatewayContainerProps: React.ComponentProps<typeof ItemPriceGatewayContainer> = {
        hideIcon: true,
        itemId,
        onMouseEnter,
        onMouseLeave,
        runFunction: () => {
            onNavigate?.();
            navigate({ pathname: itemUrl });
        },
    };

    let returnComponent: React.ReactNode = null;
    const TypographyComponent = shouldUseCarouselFontSize ? H5 : H2;

    switch (displayKind) {
        case ItemCardPriceRowDisplayKind.WithdrawnPaused:
            returnComponent = (
                <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                    <span {...spanProps}>
                        <H2 className={PRICE_LINK_CLASS}>{formatMessage(['lotWithdrawn'])}</H2>
                    </span>
                </ItemPriceGatewayContainer>
            );
            break;
        case ItemCardPriceRowDisplayKind.ActivePrice:
            returnComponent = (
                <span {...spanProps}>
                    <SubtleMonochromeLink
                        className="flex items-baseline justify-center"
                        href={itemUrl}
                        onClick={() => onNavigate?.()}
                    >
                        <TypographyComponent className={cn(PRICE_LINK_CLASS, isHovered && PRICE_LINK_HOVERED_CLASS)}>
                            <FormattedCurrency
                                convert
                                noFraction
                                sourceCurrency={currency}
                                // current price unless buy now available PLUS is not lot open (preserves old order of operations)
                                value={buyNowAvailable && !isLotOpen ? buyNowPrice : currentPrice}
                            />
                        </TypographyComponent>
                        {showBidCount &&
                            (isMobile ? (
                                <Caption className="ml-2xs text-text-secondary">
                                    {formatMessage(['itemBidding', 'bid'], {
                                        values: { bidCount },
                                    })}
                                </Caption>
                            ) : (
                                <BodySecondary className="ml-2xs text-text-secondary">
                                    {formatMessage(['itemBidding', 'bid'], {
                                        values: { bidCount },
                                    })}
                                </BodySecondary>
                            ))}
                    </SubtleMonochromeLink>
                </span>
            );
            break;
        case ItemCardPriceRowDisplayKind.SoldReserveMet:
            returnComponent = (
                <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                    <span {...spanProps}>
                        <TypographyComponent
                            className={cn(PRICE_LINK_CLASS, 'cursor-pointer', isHovered && PRICE_LINK_HOVERED_CLASS)}
                        >
                            {salePrice
                                ? formatMessage(['sold_for'], {
                                      values: {
                                          amount: (
                                              <FormattedCurrency
                                                  key="sold-for-amount"
                                                  noFraction
                                                  sourceCurrency={currency}
                                                  value={salePrice}
                                              />
                                          ),
                                      },
                                  })
                                : formatMessage(['contact_seller_for_price'])}
                        </TypographyComponent>
                    </span>
                </ItemPriceGatewayContainer>
            );
            break;
        case ItemCardPriceRowDisplayKind.SoldNoReserve:
            returnComponent = (
                <ItemPriceGatewayContainer
                    {...itemPriceGatewayContainerProps}
                    runFunction={undefined}
                >
                    <span {...spanProps}>
                        <SubtleMonochromeLink
                            href={itemUrl}
                            onClick={() => onNavigate?.()}
                        >
                            <TypographyComponent
                                className={cn(PRICE_LINK_CLASS, isHovered && PRICE_LINK_HOVERED_CLASS)}
                            >
                                {(isCatalogOnly || isOnSearchPage) && formatMessage(['lot_closed'])}
                            </TypographyComponent>
                        </SubtleMonochromeLink>
                    </span>
                </ItemPriceGatewayContainer>
            );
            break;
        case ItemCardPriceRowDisplayKind.CatalogPreview:
            returnComponent = (
                <span {...spanProps}>
                    <SubtleMonochromeLink
                        href={itemUrl}
                        onClick={() => onNavigate?.()}
                    >
                        <TypographyComponent className={cn(PRICE_LINK_CLASS, isHovered && PRICE_LINK_HOVERED_CLASS)}>
                            {formatMessage(['registration', 'preview_only'])}
                        </TypographyComponent>
                    </SubtleMonochromeLink>
                </span>
            );
            break;
        case ItemCardPriceRowDisplayKind.UnsoldPending:
            returnComponent = (
                <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                    <span {...spanProps}>
                        <TypographyComponent className={cn(PRICE_LINK_CLASS, isHovered && PRICE_LINK_HOVERED_CLASS)}>
                            {formatMessage(['lot_unsold_pending'])}
                        </TypographyComponent>
                    </span>
                </ItemPriceGatewayContainer>
            );
            break;
        case ItemCardPriceRowDisplayKind.Passed:
            returnComponent = (
                <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                    <span {...spanProps}>
                        <TypographyComponent className={cn(PRICE_LINK_CLASS, isHovered && PRICE_LINK_HOVERED_CLASS)}>
                            {isCatalogOnly
                                ? formatMessage(['lot_closed'])
                                : formatMessage(['itemBidding', 'passedText'])}
                        </TypographyComponent>
                    </span>
                </ItemPriceGatewayContainer>
            );
            break;
        default:
            break;
    }

    return <section className={className}>{returnComponent}</section>;
}
